<template>
  <div class="phonebot-container">
    <!-- ✅ HEADER MIT DELETE BUTTON AUSSERHALB DER TABS -->
    <div class="phonebot-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-1">
            <i class="fas fa-robot me-2 text-primary"></i>
            Phonebot Konfiguration
          </h4>
          <p class="text-muted mb-0">
            {{ vbot.bezeichnung || 'Unbenannter Phonebot' }}
            <span v-if="vbot.license_Key" class="badge bg-light text-dark ms-2">
              {{ vbot.license_Key }}
            </span>
          </p>
        </div>
        
        <!-- DELETE BUTTON IM HEADER -->
        <div class="header-actions">
          <button 
            class="btn btn-outline-danger" 
            type="button" 
            @click="deleteItem(vbot.id)"
            title="Phonebot löschen"
          >
            <i class="fas fa-trash me-2"></i>
            Phonebot löschen
          </button>
        </div>
      </div>
    </div>

    <!-- Hauptformular -->
    <div class="p-4">
      <Form role="form" @submit="handleUpdate">
        
        <!-- ✅ CUSTOM TAB NAVIGATION (Vue-basiert) -->
        <ul class="nav nav-tabs" role="tablist">
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'general' }"
              @click.prevent="setActiveTab('general')"
              href="#"
            >
              Allgemein
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'abo-info' }"
              @click.prevent="setActiveTab('abo-info')"
              href="#"
            >
              Abo-Info
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'sip' }"
              @click.prevent="setActiveTab('sip')"
              href="#"
            >
              SIP-Einstellungen
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'ai' }"
              @click.prevent="setActiveTab('ai')"
              href="#"
            >
              KI-Modelle
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'network' }"
              @click.prevent="setActiveTab('network')"
              href="#"
            >
              Netzwerk
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'email' }"
              @click.prevent="setActiveTab('email')"
              href="#"
            >
              E-Mail
            </a>
          </li>
          <li class="nav-item">
            <a 
              class="nav-link" 
              :class="{ active: activeTab === 'advanced' }"
              @click.prevent="setActiveTab('advanced')"
              href="#"
            >
              Erweitert
            </a>
          </li>
        </ul>

        <!-- ✅ TAB CONTENT (Vue-basiert) -->
        <div class="tab-content mt-4">
          
          <!-- ALLGEMEIN TAB -->
          <div v-if="activeTab === 'general'" class="tab-pane active">
            <h5 class="mb-3">📋 Allgemeine Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Bezeichnung
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Bezeichnung</strong>
                        Der eindeutige Name für Ihren Phonebot.
                      </div>
                    </span>
                  </label>
                  <Field type="text" class="form-control" v-model="vbot.bezeichnung" name="bezeichnung" id="bezeichnung" />
                  <ErrorMessage name="bezeichnung" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Product Name
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Produktversion</strong>
                        Basic: Grundfunktionen | Advanced: Erweiterte Features
                      </div>
                    </span>
                  </label>
                  <Field as="select" class="form-control" v-model="vbot.product_name" name="product_name" id="product_name">
                    <option value="" disabled>Select Product Name</option>
                    <option value="KI-Phone-Basic">KI-Phone-Basic</option>
                    <option value="KI-Phone-Advanced">KI-Phone-Advanced</option>
                  </Field>
                  <ErrorMessage name="product_name" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>
                    Ansagetext
                    <span class="info-icon">
                      i
                      <div class="info-tooltip">
                        <strong>Begrüßungstext</strong>
                        Der erste Text, den Anrufer hören.
                      </div>
                    </span>
                  </label>
                  <textarea class="form-control" v-model="vbot.ansagetext" name="ansagetext" id="ansagetext" rows="3"></textarea>
                  <ErrorMessage name="ansagetext" class="text-danger" />
                </div>
              </div>
            </div>
            
            <!-- ✅ ENHANCED PROMPT OVERLAY SEKTION -->
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4" v-if="!showPromptOverlay">
                  <label>Prompt</label>
                  <textarea
                    class="form-control prompt-trigger"
                    v-model="vbot.prompt"
                    name="prompt"
                    id="prompt"
                    rows="8"
                    @click="openPromptOverlay"
                    @focus="openPromptOverlay"
                    placeholder="Klicken zum Erweitern..."
                    readonly
                    style="cursor: pointer;"
                  ></textarea>
                  <small class="text-muted">💡 Klicken Sie hier, um den erweiterten Editor zu öffnen</small>
                </div>

                <!-- ✅ ENHANCED VOLLBILD-OVERLAY -->
                <div v-if="showPromptOverlay" class="prompt-fullscreen-overlay">
                  <div class="prompt-fullscreen-container">
                    <!-- Header -->
                    <div class="prompt-header">
                      <h4 class="mb-0">
                        <i class="fas fa-edit me-2"></i>
                        Prompt Editor
                      </h4>
                      <div class="header-actions d-flex align-items-center">
                        <!-- ✅ AI GENERATION BUTTON -->
                        <button 
                          type="button" 
                          class="btn btn-sm btn-outline-light me-2"
                          @click="toggleAiMode"
                          :title="showAiMode ? 'Einzelansicht' : 'KI-Assistent aktivieren'"
                        >
                          <i :class="showAiMode ? 'fas fa-columns' : 'fas fa-magic'"></i>
                        </button>
                        
                        <button 
                          type="button" 
                          class="btn btn-sm btn-outline-light me-2"
                          @click="closePromptOverlay"
                          title="Minimieren"
                        >
                          <i class="fas fa-minus"></i>
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-sm btn-light"
                          @click="closePromptOverlay"
                          title="Schließen"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <!-- ✅ ENHANCED CONTENT -->
                    <div class="prompt-content">
                      <!-- ✅ SINGLE VIEW (Normal) -->
                      <div v-if="!showAiMode" class="single-view">
                        <textarea
                          class="form-control prompt-fullscreen-textarea"
                          v-model="vbot.prompt"
                          name="prompt"
                          ref="fullscreenPrompt"
                          placeholder="Geben Sie hier Ihren Prompt ein..."
                        ></textarea>
                      </div>

                      <!-- ✅ DUAL VIEW (AI-Modus) -->
                      <div v-else class="dual-view">
                        <div class="row h-100">
                          <!-- Original Prompt (Links) -->
                          <div class="col-md-6 prompt-column">
                            <div class="prompt-section h-100">
                              <h6 class="prompt-section-title">
                                <i class="fas fa-edit me-2"></i>
                                Prompt
                              </h6>
                              <textarea
                                class="form-control prompt-section-textarea"
                                v-model="vbot.prompt"
                                name="original_prompt"
                                placeholder="Geben Sie hier Ihren Prompt ein..."
                              ></textarea>
                            </div>
                          </div>

                          <!-- Generierter Prompt (Rechts) -->
                          <div class="col-md-6 prompt-column">
                            <div class="prompt-section h-100">
                              <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="prompt-section-title mb-0">
                                  <i class="fas fa-robot me-2 text-primary"></i>
                                  Generierter Prompt
                                </h6>
                                <!-- ✅ FUTURE: Generate Button -->
                                <button 
                                  type="button" 
                                  class="btn btn-sm btn-primary"
                                  @click="generatePrompt"
                                  :disabled="isGenerating"
                                  title="Prompt generieren"
                                >
                                  <i :class="isGenerating ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
                                  {{ isGenerating ? 'Generiert...' : 'Generieren' }}
                                </button>
                              </div>
                              <textarea
                                class="form-control prompt-section-textarea generated-prompt"
                                v-model="generatedPrompt"
                                name="generated_prompt"
                                placeholder="Hier erscheint der generierte Prompt..."
                                readonly
                              ></textarea>
                              
                              <!-- ✅ FUTURE: Copy/Apply Buttons -->
                              <div class="mt-2 d-flex gap-2" v-if="generatedPrompt">
                                <button 
                                  type="button" 
                                  class="btn btn-sm btn-outline-secondary"
                                  @click="copyGeneratedPrompt"
                                  title="Generierten Prompt kopieren"
                                >
                                  <i class="fas fa-copy me-1"></i>
                                  Kopieren
                                </button>
                                <button 
                                  type="button" 
                                  class="btn btn-sm btn-success"
                                  @click="applyGeneratedPrompt"
                                  title="Generierten Prompt übernehmen"
                                >
                                  <i class="fas fa-arrow-left me-1"></i>
                                  Übernehmen
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Footer -->
                    <div class="prompt-footer">
                      <div class="d-flex justify-content-between align-items-center">
                        <div class="text-muted small">
                          <span v-if="!showAiMode">
                            Zeichen: {{ (vbot.prompt || '').length }} | ESC zum Schließen
                          </span>
                          <span v-else>
                            Original: {{ (vbot.prompt || '').length }} Zeichen | 
                            Generiert: {{ (generatedPrompt || '').length }} Zeichen | 
                            ESC zum Schließen
                          </span>
                        </div>
                        <div>
                          <button 
                            type="button" 
                            class="btn btn-outline-secondary me-2"
                            @click="closePromptOverlay"
                          >
                            Schließen
                          </button>
                          <button 
                            type="button" 
                            class="btn btn-success"
                            @click="saveAndClose"
                          >
                            <i class="fas fa-save me-1"></i>
                            Fertig
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Backdrop -->
                <div 
                  v-if="showPromptOverlay" 
                  class="prompt-backdrop"
                  @click="closePromptOverlay"
                ></div>
              </div>
            </div>
          </div>

          <!-- ✅ ABO-INFO TAB (OHNE DELETE BUTTON) -->
          <div v-if="activeTab === 'abo-info'" class="tab-pane active">
            <h5 class="mb-3">💳 Abonnement Information</h5>
            
            <div class="row">
              <div class="col-md-12">
                <div class="card border-0 shadow-sm">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="fas fa-crown me-2 text-warning"></i>
                      Abonnement Status
                    </h5>
                  </div>
                  
                  <div class="row">
                    <div class="col-md-12">
                      <label>Status:</label>
                      <span
                        :class="statusClass"
                        class="badge px-3 py-2"
                      >
                        {{ statusText }}
                      </span>
                    </div>
                  </div>
                  <div class="row">
                    <div class="checkout-container"></div>
                    <button type="button" @click="openCheckout" v-if="vbot.payment_status === 0">Checkout Paddle öffnen</button>
                </div>    
                </div>

                
              </div>
            </div>
          </div>

          <!-- SIP TAB -->
          <div v-if="activeTab === 'sip'" class="tab-pane active">
            <h5 class="mb-3">📞 SIP-Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Benutzername</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_benutzername" name="sip_benutzername" id="sip_benutzername" />
                  <ErrorMessage name="sip_benutzername" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Password</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_passwd" name="sip_passwd" id="sip_passwd" />
                  <ErrorMessage name="sip_passwd" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Registername</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_registername" name="sip_registername" id="sip_registername" />
                  <ErrorMessage name="sip_registername" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Telefonnummer</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_phonenumber" name="sip_phonenumber" id="sip_phonenumber" />
                  <ErrorMessage name="sip_phonenumber" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Server</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_server" name="sip_server" id="sip_server" />
                  <ErrorMessage name="sip_server" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SIP Port</label>
                  <Field type="text" class="form-control" v-model="vbot.sip_port" name="sip_port" id="sip_port" />
                  <ErrorMessage name="sip_port" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row">
            <div class="col-md-6">
              <div class="input-group input-group-static mb-4">
                <label>
                  SIP Registrierungsintervall (Sekunden)
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>Registrierungsintervall</strong>
                      Zeitspanne, nach der sich der SIP-Client automatisch neu beim Server anmeldet, um die Verbindung aktiv zu halten.
                    </div>
                  </span>
                </label>
                <Field
                  type="text"
                  class="form-control"
                  v-model="vbot.sip_registrierungsinterval"
                  name="sip_registrierungsinterval"
                  id="sip_registrierungsinterval"
                />
                <ErrorMessage name="sip_registrierungsinterval" class="text-danger" />
              </div>
            </div>
            
          </div>

            <!-- Transport Type und weitere SIP Einstellungen -->
            <div class="row">
              
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>SDK</label>
                  <Field as="select" class="form-control" v-model="vbot.sdk" name="sdk" id="sdk">
                    <option value="" disabled>Select SDK</option>
                    <option value="PortSIP">PortSIP</option>
                    <option value="Ozeki">Ozeki</option>
                  </Field>
                  <ErrorMessage name="sdk" class="text-danger" />
                </div>
              </div>
            </div>
            

            <!-- Checkboxes für SIP -->
            
          </div>

          <!-- EMAIL TAB -->
          <div v-if="activeTab === 'email'" class="tab-pane active">
            <h5 class="mb-3">📧 E-Mail-Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email Server</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_server" name="e_mail_server" id="e_mail_server" />
                  <ErrorMessage name="e_mail_server" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email Server Port</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_server_port" name="e_mail_server_port" id="e_mail_server_port" />
                  <ErrorMessage name="e_mail_server_port" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email User</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_user" name="e_mail_user" id="e_mail_user" />
                  <ErrorMessage name="e_mail_user" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Email Password</label>
                  <Field type="password" class="form-control" v-model="vbot.e_mail_passwd" name="e_mail_passwd" id="e_mail_passwd" />
                  <ErrorMessage name="e_mail_passwd" class="text-danger" />
                </div>
              </div>
              
            </div>
            
            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Email Server From</label>
                  <Field type="text" class="form-control" v-model="vbot.e_mail_server_from" name="e_mail_server_from" id="e_mail_server_from" />
                  <ErrorMessage name="e_mail_server_from" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="mail_certifikate_validation">Mail Certificate Validation</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.mail_certifikate_validation"
                      name="mail_certifikate_validation"
                      id="mail_certifikate_validation"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI TAB -->
          <div v-if="activeTab === 'ai'" class="tab-pane active">
            <h5 class="mb-3">🤖 KI-Modelle & Einstellungen</h5>
            
            <!-- Chat Model Selection -->
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Chat Model</label>
                  <Field as="select" class="form-control" v-model="vbot.chatmodel" name="chatmodel" id="chatmodel">
                    <option value="gpt-4o" selected>gpt-4o</option>
                    <option value="gpt-5">gpt-5</option>
                    <option value="gpt-5-mini">gpt-5-mini</option>
                    <option value="gpt-5-nano">gpt-5-nano</option>
                    <option value="gpt-4.1">gpt-4.1</option>
                    <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                    <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4-turbo">gpt-4-turbo</option>
                    <option value="gpt-4">gpt-4</option>
                    <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
                    <option value="o1">o1</option>
                    <option value="o1-mini">o1-mini</option>
                    <option value="o3">o3</option>
                    <option value="o3-mini">o3-mini</option>
                    <option value="o4-mini">o4-mini</option>
                    <option value="gpt-realtime">gpt-realtime</option>
                    <option value="gpt-4o-realtime-preview">gpt-4o-realtime-preview</option>
                    <option value="gpt-4o-mini-realtime-preview">gpt-4o-mini-realtime-preview</option>
                    <option value="gpt-realtime-mini">gpt-realtime-mini</option>
                  </Field>
                  <ErrorMessage name="chatmodel" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>STT Model</label>
                  <Field as="select" class="form-control" v-model="vbot.sttmodel" name="sttmodel" id="sttmodel">
                    <option value="OpenAI" selected>OpenAI</option>
                    <option value="Microsoft">Microsoft</option>
                    <option value="Voxtral">Voxtral</option>
                  </Field>
                  <ErrorMessage name="sttmodel" class="text-danger" />
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TTS Model</label>
                  <Field as="select" class="form-control" v-model="vbot.ttsmodel" name="ttsmodel" id="ttsmodel">
                    <option value="OpenAI" selected>OpenAI</option>
                    <option value="Microsoft">Microsoft</option>
                  </Field>
                  <ErrorMessage name="ttsmodel" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Voice für OpenAI</label>
                  <Field as="select" class="form-control" v-model="vbot.voice" name="voice" id="voice">
                    <option value="shimmer">Shimmer</option>
                    <option value="ash">Ash</option>
                    <option value="echo">Echo</option>
                    <option value="ballad">Ballad</option>
                    <option value="verse">Verse</option>
                    <option value="sage">Sage</option>
                    <option value="coral">Coral</option>
                  </Field>
                  <ErrorMessage name="voice" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Open AI Key</label>
                  <Field type="text" class="form-control" v-model="vbot.apen_ai_key" name="apen_ai_key" id="apen_ai_key" />
                  <ErrorMessage name="apen_ai_key" class="text-danger" />
                </div>
              </div>
            </div>
            <div class="row">
            <div class="col-md-6">
              <div class="input-group input-group-static mb-4">
                <label>
                  Temperatur
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>KI-Kreativität</strong>
                      0.1-0.3: Konsistent | 0.5-0.8: Ausgewogen | 1.0+: Kreativ<br>
                      <strong>Bereich:</strong> Realtime: 0.6-1.2 | Standard: 0.0-2.0
                    </div>
                  </span>
                </label>
                <Field
                  type="number"
                  class="form-control"
                  v-model="vbot.temperature"
                  name="temperature"
                  id="temperature"
                  :min="temperatureMin"
                  :max="temperatureMax"
                  :step="0.1"
                />
                <small class="form-text text-muted">
                  Erlaubter Bereich: {{ temperatureMin }} - {{ temperatureMax }}
                </small>
                <ErrorMessage name="temperature" class="text-danger" />
              </div>
            </div>
          </div>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="forwarding_through_openai_tool">Forwarding Through OpenAI Tool</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.forwarding_through_openai_tool"
                      name="forwarding_through_openai_tool"
                      id="forwarding_through_openai_tool"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="websearch_openai">Activate Websearch in OpenAI</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.websearch_openai"
                      name="websearch_openai"
                      id="websearch_openai"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- MCP Server (falls aktiviert) -->
            <div class="row" v-if="vbot.forwarding_through_openai_tool === true">
              <div class="col-md-12">
                <label>MCP Server</label>
                <div v-for="(server, index) in vbot.mcp_servers" :key="index" class="d-flex mb-2 align-items-center">
                  <input v-model="server.url" class="form-control me-2" placeholder="Server URL" />
                  <button type="button" class="btn btn-danger btn-sm" @click="removeMcpServer(index)" v-if="vbot.mcp_servers.length > 0">
                    <i class="fas fa-minus"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-primary btn-sm mt-2" @click="addMcpServer">
                  <i class="fas fa-plus"></i> MCP Server hinzufügen
                </button>
              </div>
            </div>

            <!-- Realtime API Einstellungen -->
            <div class="row" v-if="vbot.realtime_api === true">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Redegeschwindigkeit</label>
                  <Field type="number" class="form-control" v-model="vbot.speed_of_speech" name="speed_of_speech" id="speed_of_speech" />
                  <ErrorMessage name="speed_of_speech" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row" v-if="vbot.realtime_api === true">
              <div class="col-md-12">
                <div class="input-group input-group-static mb-4">
                  <label>Transcription of Language</label>
                  <Field as="select" class="form-control" v-model="vbot.transcription_language" name="transcription_language" id="transcription_language">
                    <option value="de">de</option>
                    <option value="en">en</option>
                    <option value="fr">fr</option>
                    <option value="it">it</option>
                    <option value="all">all</option>
                  </Field>
                  <ErrorMessage name="transcription_language" class="text-danger" />
                </div>
              </div>
            </div>
          </div>

          <!-- NETWORK TAB -->
          <div v-if="activeTab === 'network'" class="tab-pane active">
            <h5 class="mb-3">🌐 Netzwerk-Einstellungen</h5>
            
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Transport Type</label>
                  <Field as="select" class="form-control" v-model="vbot.transmit_type" name="transmit_type" id="transmit_type">
                    <option value="" disabled>Select TransportType</option>
                    <option value="tcp">tcp</option>
                    <option value="udp">udp</option>
                    <option value="tls">tls</option>
                    <option value="stcp">stcp</option>
                  </Field>
                  <ErrorMessage name="transmit_type" class="text-danger" />
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>STUN Server</label>
                  <Field type="text" class="form-control" v-model="vbot.stun_server" name="stun_server" id="stun_server" />
                  <ErrorMessage name="stun_server" class="text-danger" />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TURN Server</label>
                  <Field type="text" class="form-control" v-model="vbot.turnserver" name="turnserver" id="turnserver" />
                  <ErrorMessage name="turnserver" class="text-danger" />
                </div>
              </div>
               <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Outbound Proxy</label>
                  <Field type="text" class="form-control" v-model="vbot.OutboundProxy" name="OutboundProxy" id="OutboundProxy" />
                  <ErrorMessage name="OutboundProxy" class="text-danger" />
                </div>
              </div>
              
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TURN Server Username</label>
                  <Field type="text" class="form-control" v-model="vbot.nat_username" name="nat_username" id="nat_username" />
                  <ErrorMessage name="nat_username" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>TURN Server Password</label>
                  <Field type="text" class="form-control" v-model="vbot.nat_password" name="nat_password" id="nat_password" />
                  <ErrorMessage name="nat_password" class="text-danger" />
                </div>
              </div>
             
            </div>
            <div class="row">       
            <div class="col-md-4">
              <div class="input-group input-group-static mb-4">
                <label class="form-check-label" for="useSRTP">
                  Use SRTP
                  <span class="info-icon">
                    i
                    <div class="info-tooltip">
                      <strong>Verschlüsselung</strong>
                      SRTP verschlüsselt Sprachdaten.
                    </div>
                  </span>
                </label>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" v-model="vbot.useSRTP" name="useSRTP" id="useSRTP" />
                </div>
              </div>
            </div>
          </div>
          </div>

          <!-- ADVANCED TAB -->
          <div v-if="activeTab === 'advanced'" class="tab-pane active">
            <h5 class="mb-3">🔧 Erweiterte Einstellungen</h5>

            
            

            <!-- URLs -->
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>PromptXT-URL</label>
                  <Field type="text" class="form-control" v-model="vbot.prompturl" name="prompturl" id="prompturl" />
                  <ErrorMessage name="prompturl" class="text-danger" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Outbound List URL</label>
                  <Field type="text" class="form-control" v-model="vbot.outbound_list_url" name="outbound_list_url" id="outbound_list_url" />
                  <ErrorMessage name="outbound_list_url" class="text-danger" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Flowise URL</label>
                  <Field type="text" class="form-control" v-model="vbot.flowiseurl" name="flowiseurl" id="flowiseurl" />
                  <ErrorMessage name="flowiseurl" class="text-danger" />
                </div>
              </div>
            <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>n8n URL</label>
                  <Field type="text" class="form-control" v-model="vbot.n8n_url" name="n8n_url" id="n8n_url" />
                  <ErrorMessage name="n8n_url" class="text-danger" />
                </div>
              </div>
            </div>
            
            <!-- Web Log Checkbox -->
            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="web_log_enabled">Web-Log</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.web_log_enabled"
                      name="web_log_enabled"
                      id="web_log_enabled"
                    />
                  </div>
                </div>
              </div>
            </div>


            <div class="row">
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label class="form-check-label" for="attendedtransfer">Attended Transfer</label>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      v-model="vbot.attendedtransfer"
                      name="attendedtransfer"
                      id="attendedtransfer"
                    />
                  </div>
                </div>
              </div> 
              <div class="col-md-6">
                <div class="input-group input-group-static mb-4">
                  <label>Time until call abort (seconds)</label>
                  <Field type="number" class="form-control" v-model="vbot.abort_time" name="abort_time" id="abort_time" />
                  <ErrorMessage name="abort_time" class="text-danger" />
                </div>
              </div>
              
            </div>
            <!-- Category Terms -->
            <div class="row">
              <div class="col-md-12">
                <div class="mb-4">
                  <label class="form-label d-block">Category Terms (Max 5)</label>
                  <div v-for="(term, index) in vbot.category_terms" :key="index" class="d-flex align-items-center mb-2">
                    <div class="flex-grow-1 input-group input-group-static">
                      <Field
                        type="text"
                        class="form-control border-0 border-bottom"
                        v-model="vbot.category_terms[index]"
                        :name="'category_terms_' + index"
                        :id="'category_terms_' + index"
                      />
                    </div>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-danger ms-2" 
                      @click="deleteCategoryTerm(index)"
                      v-if="vbot.category_terms.length > 1"
                    >
                      <i class="fas fa-minus"></i>
                    </button>
                  </div>
                  <ErrorMessage name="category_terms" class="text-danger" />
                  <button
                    v-if="vbot.category_terms.length < 5"
                    type="button"
                    class="btn btn-sm btn-primary mt-2"
                    @click="addCategoryTerm"
                  >
                    <i class="fas fa-plus"></i> Add Term
                  </button>
                  <div v-else>
                    <small class="text-muted">Maximum 5 category terms allowed.</small>
                  </div>
                </div>
              </div>
            </div>

            
          </div>


        <!-- Hidden Fields -->
        <Field type="hidden" class="form-control" v-model="vbot.id" name="id" id="id" />

        <!-- Submit Button -->
        <div class="button-row d-flex justify-content-end mt-4">
          <button class="btn btn-dark" type="submit">
            Update Phonebot
          </button>
        </div>
      </div>
    </Form>
  </div>
</div>
</template>

<script>
import { Form, Field, ErrorMessage } from "vee-validate";
import showSwal from "@/mixins/showSwal";
import Swal from 'sweetalert2';

export default {
  name: "Info4",
  
  components: {
    Form,
    Field,
    ErrorMessage
  },
  
  data() {
    return {
      // Tab-Steuerung
      activeTab: 'general',
      
      // Enhanced Overlay-Steuerung
      showPromptOverlay: false,
      showAiMode: false,
      originalPrompt: '',
      generatedPrompt: 'generierter Prompt Text',
      isGenerating: false,
      
      // VBot-Daten
      vbot: {
        // Basic Info
        id: '',
        bezeichnung: '',
        product_name: '',
        license_Key: '',
        payment_status: 0,
        
        // Content
        ansagetext: '',
        prompt: '',
        
        // SIP Settings
        sip_benutzername: '',
        sip_passwd: '',
        sip_registername: '',
        sip_phonenumber: '',
        sip_server: '',
        sip_port: '',
        transmit_type: '',
        attendedtransfer: false,
        useSRTP: false,
        sdk: '',
        abort_time: '',
        
        // Email Settings
        e_mail_server: '',
        e_mail_user: '',
        e_mail_passwd: '',
        e_mail_server_port: '',
        e_mail_server_from: '',
        mail_certifikate_validation: false,
        
        // AI Models
        chatmodel: "gpt-4o",
        sttmodel: "OpenAI",
        ttsmodel: "OpenAI",
        voice: "Shimmer",
        apen_ai_key: '',
        
        // Network
        prompturl: '',
        stun_server: '',
        turnserver: '',
        nat_username: '',
        nat_password: '',
        OutboundProxy: '',
        outbound_list_url: '',
        
        // Advanced
        web_log_enabled: true,
        category_terms: [],
        flowiseurl: '',
        n8n_url: '',
        
        // AI Features
        forwarding_through_openai_tool: false,
        websearch_openai: false,
        mcp_servers: [{ url: '' }],
        
        // Realtime API
        realtime_api: false,
        temperature: '',
        speed_of_speech: '',
        transcription_language: 'de'
      }
    };
  },
  
  computed: {
    temperatureMin() {
      return this.vbot.chatmodel && this.vbot.chatmodel.includes('realtime') ? 0.6 : 0;
    },
    temperatureMax() {
      return this.vbot.chatmodel && this.vbot.chatmodel.includes('realtime') ? 1.2 : 2;
    },
    statusText() {
      switch (this.vbot.payment_status) {
        case 0: return 'Inaktiv';
        case 1: return 'Aktiv';
        case 2: return 'Pausiert';
        case 3: return 'Zahlungsrückstand';
        case 4: return 'Gekündigt';
        default: return 'Unbekannt';
      }
    },
    statusClass() {
      switch (this.vbot.payment_status) {
        case 0: return 'bg-secondary text-white';
        case 1: return 'bg-success text-white';
        case 2: return 'bg-warning text-dark';
        case 3: return 'bg-danger text-white';
        case 4: return 'bg-dark text-white';
        default: return 'bg-secondary text-white';
      }
    },
  },

  methods: {
    // Tab-Steuerung
    setActiveTab(tabName) {
      this.activeTab = tabName;
    },

    // Enhanced Prompt Overlay Methoden
    openPromptOverlay() {
      this.originalPrompt = this.vbot.prompt;
      this.showPromptOverlay = true;
      this.showAiMode = false;
      this.$nextTick(() => {
        this.$refs.fullscreenPrompt?.focus();
        document.body.style.overflow = 'hidden';
      });
    },

    closePromptOverlay() {
      this.showPromptOverlay = false;
      this.showAiMode = false;
      document.body.style.overflow = '';
    },

    saveAndClose() {
      this.closePromptOverlay();
    },

    handleEscape(event) {
      if (event.key === 'Escape' && this.showPromptOverlay) {
        this.closePromptOverlay();
      }
    },

    // AI-Modus Methoden
    toggleAiMode() {
      this.showAiMode = !this.showAiMode;
      if (this.showAiMode && !this.generatedPrompt) {
        this.generatedPrompt = 'generierter Prompt Text';
      }
    },

    async generatePrompt() {

  // ✅ FALLBACK: Verwende license_Key wenn id fehlt
  let vbotId = this.vbot.id;
  
  if (!vbotId) {
    console.warn('⚠️ VBot ID fehlt, verwende license_Key als Fallback');
    vbotId = this.vbot.license_Key || this.$route.params.license_Key;
  }

  if (!vbotId) {
    showSwal.methods.showSwal({
      type: "error",
      message: "Fehler: VBot-ID nicht verfügbar. Bitte laden Sie die Seite neu.",
      width: 500,
      timer: 4000
    });
    return;
  }

  console.log('✅ Verwende VBot-ID:', vbotId);
  this.isGenerating = true;
  
  try {
    const promptData = {
      current_prompt: this.vbot.prompt || ''
    };

    const response = await this.$store.dispatch('vbot/generatePrompt', {
      vbotId: vbotId,  // ✅ Verwende die validierte ID
      promptData: promptData
    });

    this.generatedPrompt = response.generated_prompt;
    
    showSwal.methods.showSwal({
      type: "success",
      message: "Prompt erfolgreich generiert!",
      width: 400,
      timer: 2000
    });
    
  } catch (error) {
    console.error('❌ Prompt generation error:', error);
    
    let errorMessage = "Fehler bei der Prompt-Generierung";
    if (error.response?.status === 404) {
      errorMessage = `API-Route nicht gefunden. VBot-ID: ${vbotId}`;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }
    
    showSwal.methods.showSwal({
      type: "error",
      message: errorMessage,
      width: 500,
      timer: 5000
    });
  } finally {
    this.isGenerating = false;
  }
},

    copyGeneratedPrompt() {
      if (navigator.clipboard && this.generatedPrompt) {
        navigator.clipboard.writeText(this.generatedPrompt).then(() => {
          showSwal.methods.showSwal({
            type: "success",
            message: "Generierter Prompt kopiert!",
            width: 400,
            timer: 2000
          });
        });
      }
    },

    async applyGeneratedPrompt() {
      if (this.generatedPrompt) {
        const result = await Swal.fire({
          title: 'Prompt übernehmen?',
          html: `
            <div class="text-start">
              <p class="mb-3"><strong>Sie sind dabei, den generierten Prompt zu übernehmen.</strong></p>
              <div class="alert alert-warning text-start mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Der Prompt wird erst <strong>nach dem Klick auf "Update Phonebot"</strong> 
                endgültig gespeichert.
              </div>
            </div>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#28a745',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Ja, übernehmen',
          cancelButtonText: 'Abbrechen',
        });

        if (result.isConfirmed) {
          this.vbot.prompt = this.generatedPrompt;
          showSwal.methods.showSwal({
            type: "success",
            message: "Prompt übernommen!",
            width: 450,
            timer: 4000
          });
        }
      }
    },

    // Bestehende Methoden
    async handleUpdate() {
      try {
        await this.$store.dispatch('vbot/updatePhonebot', this.vbot);
        showSwal.methods.showSwal({
          type: "success",
          message: "Phonebot Updated Successfully",
          width: 500,
          timer: 160000,
          showConfirmButton: true
        });
        this.$router.push({ name: 'phonebots' });
      } catch (error) {
        console.error('Phonebot error:', error);
        showSwal.methods.showSwal({
          type: "error",
          message: "Error In Updating Phonebot!",
          width: 500
        });
      }
    },

    async deleteItem(vid) {
      const result = await Swal.fire({
        title: 'Sind Sie sicher?',
        text: "Diese Aktion kann nicht rückgängig gemacht werden!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen'
      });

      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('vbot/deleteVbot', vid);
          Swal.fire('Gelöscht!', 'Der Phonebot wurde erfolgreich gelöscht.', 'success');
          this.$router.push({ name: 'phonebots' });
        } catch (error) {
          console.error(`Error deleting phonebot with ID: ${vid}`, error);
          Swal.fire('Fehler!', 'Beim Löschen ist ein Fehler aufgetreten.', 'error');
        }
      }
    },

    addCategoryTerm() {
      if (this.vbot.category_terms.length < 5) {
        this.vbot.category_terms.push("");
      }
    },

    deleteCategoryTerm(index) {
      if (this.vbot.category_terms.length > 1) {
        this.vbot.category_terms.splice(index, 1);
      }
    },

    addMcpServer() {
      this.vbot.mcp_servers.push({ url: '' });
    },

    removeMcpServer(idx) {
      if (this.vbot.mcp_servers.length > 0) {
        this.vbot.mcp_servers.splice(idx, 1);
      }
    },

    openCheckout() {
      if (window.Paddle) {
        window.Paddle.Checkout.open({
          items: [{
            priceId: 'pri_01k367k95yg378z5gk5jbf6fwq',
            quantity: 1,
          }],
          customData: {
            license_key: this.vbot.license_Key
          },
        });
      }
    }
  },

  async mounted() {
    // Event Listener
    document.addEventListener('keydown', this.handleEscape);

    const licenseKey = this.$route.params.license_Key;

    // Paddle-Script laden
    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.onload = () => {
      if (window.Paddle) {
        window.Paddle.Environment.set("sandbox");
        window.Paddle.Initialize({
          token: "test_29aed94bafd8aa545b89c19df4f",
          checkout: {
            settings: {
              displayMode: "inline",
              frameTarget: "checkout-container",
              frameInitialHeight: "450",
              frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;"
            }
          }
        });
      }
    };
    document.body.appendChild(script);

    // VBot Daten laden
    try {
      const vbots = await this.$store.dispatch('vbot/getVbotById', licenseKey);
      this.vbot = {
        ...vbots,
        mail_certifikate_validation: vbots.mail_certifikate_validation === "1" || vbots.mail_certifikate_validation === 1,
        attendedtransfer: vbots.attendedtransfer === "1" || vbots.attendedtransfer === 1,
        useSRTP: vbots.useSRTP === "1" || vbots.useSRTP === 1,
        realtime_api: vbots.realtime_api === "1" || vbots.realtime_api === 1,
        web_log_enabled: vbots.web_log_enabled === "1" || vbots.web_log_enabled === 1,
        forwarding_through_openai_tool: vbots.forwarding_through_openai_tool === "1" || vbots.forwarding_through_openai_tool === 1,
        websearch_openai: vbots.websearch_openai === "1" || vbots.websearch_openai === 1,
        category_terms: Array.isArray(vbots.category_terms) ? vbots.category_terms : [],
        mcp_servers: Array.isArray(vbots.mcp_servers) && vbots.mcp_servers.length > 0 ? vbots.mcp_servers : [],
        sttmodel: vbots.sttmodel || "OpenAI",
        ttsmodel: vbots.ttsmodel || "OpenAI",
        chatmodel: vbots.chatmodel || "gpt-4o",
        voice: vbots.voice ? vbots.voice.toLowerCase() : "shimmer"
      };
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscape);
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/* ✅ HEADER STYLING */
.phonebot-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.phonebot-header h4 {
  color: #2c3e50;
  font-weight: 600;
}

.phonebot-header .text-muted {
  font-size: 0.9rem;
}

.header-actions .btn {
  font-weight: 500;
  padding: 8px 16px;
}

/* ✅ SICHERSTELLUNG DASS SWEETALERT2 ÜBER PROMPT-OVERLAY ERSCHEINT */
:deep(.swal-high-z-index) {
  z-index: 10001 !important;
}

/* ✅ GLOBAL SWEETALERT2 Z-INDEX FIX */
:global(.swal2-container) {
  z-index: 10001 !important;
}

:global(.swal2-backdrop-show) {
  z-index: 10001 !important;
}

/* Weißer Hintergrund für alle Input-Felder */
.form-control {
  background-color: #ffffff !important;
}

/* Weißer Hintergrund für Textareas */
textarea.form-control {
  background-color: #ffffff !important;
}

/* Weißer Hintergrund für Select-Felder */
select.form-control {
  background-color: #ffffff !important;
}

/* Weißer Hintergrund für den gesamten Tab-Content-Bereich */
.tab-content {
  background-color: #ffffff;
}

.tab-pane {
  background-color: #ffffff;
  padding: 20px 0;
}

.input-group-static label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #17a2b8;
  color: white;
  font-size: 11px;
  font-weight: bold;
  cursor: help;
  position: relative;
  transition: all 0.3s;
}

.info-icon:hover {
  background: #138496;
  transform: scale(1.1);
}

.info-tooltip {
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background: #2c3e50;
  color: white;
  padding: 10px 15px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: normal;
  white-space: normal;
  width: 250px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  line-height: 1.4;
}

.info-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #2c3e50;
}

.info-icon:hover .info-tooltip {
  opacity: 1;
  visibility: visible;
  bottom: 135%;
}

.info-tooltip strong {
  display: block;
  margin-bottom: 5px;
  color: #3498db;
}

/* Tab Navigation */
.nav-tabs .nav-link {
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
  color: #007bff;
  background-color: #f8f9fa;
}

.nav-tabs .nav-link.active {
  font-weight: 600;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  color: #007bff;
}

/* Prompt Trigger Styles */
.prompt-trigger {
  transition: all 0.3s ease;
  border: 2px dashed #dee2e6 !important;
}

.prompt-trigger:hover {
  background-color: #f8f9fa !important;
  border-color: #007bff !important;
  transform: scale(1.01);
}

.prompt-trigger:focus {
  background-color: #f8f9fa !important;
  border-color: #007bff !important;
}

/* ✅ ENHANCED PROMPT EDITOR STYLES */

/* Backdrop - absolut im Vordergrund */
.prompt-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  backdrop-filter: blur(3px);
}

/* Hauptcontainer - über allem */
.prompt-fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

/* Editor Container */
.prompt-fullscreen-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1400px; /* Breiter für Dual-View */
  height: 85vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  animation: slideInScale 0.4s ease;
  overflow: hidden;
}

/* Header */
.prompt-header {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.prompt-header .header-actions {
  gap: 8px;
}

/* Content Bereich */
.prompt-content {
  flex: 1;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Single View (Normal) */
.single-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Dual View (AI-Modus) */
.dual-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dual-view .row {
  flex: 1;
  margin: 0;
}

.prompt-column {
  padding: 0 10px;
  height: 100%;
}

.prompt-column:first-child {
  border-right: 2px solid #e9ecef;
  padding-right: 15px;
}

.prompt-column:last-child {
  padding-left: 15px;
}

/* Prompt Sections */
.prompt-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.prompt-section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

.prompt-section-textarea {
  flex: 1;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  outline: none;
  transition: border-color 0.3s ease;
  min-height: 300px;
}

.prompt-section-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Generated Prompt Specific */
.generated-prompt {
  background-color: #f8f9fa !important;
  border-color: #28a745 !important;
}

.generated-prompt:focus {
  border-color: #28a745 !important;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1) !important;
}

/* Regular Textarea (Single View) */
.prompt-fullscreen-textarea {
  flex: 1;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: none;
  outline: none;
  transition: border-color 0.3s ease;
}

.prompt-fullscreen-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Footer */
.prompt-footer {
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

/* Abo-Info Card Styles */
.card {
  border-radius: 8px;
}

.card-header {
  border-radius: 8px 8px 0 0;
}

/* Animationen */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInScale {
  from { 
    opacity: 0;
    transform: scale(0.9) translateY(-30px);
  }
  to { 
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .phonebot-header {
    padding: 15px;
  }
  
  .phonebot-header .d-flex {
    flex-direction: column;
    gap: 15px;
    align-items: stretch !important;
  }
  
  .prompt-fullscreen-overlay {
    padding: 10px;
  }
  
  .prompt-fullscreen-container {
    height: 95vh;
    max-height: none;
    max-width: none;
  }
  
  .prompt-header {
    padding: 12px 15px;
  }
  
  .prompt-content {
    padding: 15px;
  }
  
  .prompt-footer {
    padding: 12px 15px;
  }
  
  /* Mobile: Stack columns vertically */
  .dual-view .prompt-column {
    padding: 0;
  }
  
  .dual-view .prompt-column:first-child {
    border-right: none;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 15px;
    margin-bottom: 15px;
  }
  
  .dual-view .prompt-column:last-child {
    padding-left: 0;
  }
}

/* Scrollbar für Textarea */
.prompt-fullscreen-textarea::-webkit-scrollbar,
.prompt-section-textarea::-webkit-scrollbar {
  width: 8px;
}

.prompt-fullscreen-textarea::-webkit-scrollbar-track,
.prompt-section-textarea::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.prompt-fullscreen-textarea::-webkit-scrollbar-thumb,
.prompt-section-textarea::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.prompt-fullscreen-textarea::-webkit-scrollbar-thumb:hover,
.prompt-section-textarea::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* ✅ CUSTOM MODAL STYLES */
:global(.apply-prompt-modal) {
  border-radius: 12px !important;
}

:global(.apply-prompt-modal .swal2-html-container) {
  margin: 0 !important;
  padding: 0 !important;
}

:global(.apply-prompt-modal .alert) {
  border-radius: 8px;
  font-size: 0.9rem;
}

:global(.apply-prompt-modal .swal2-actions) {
  gap: 10px;
}

:global(.apply-prompt-modal .swal2-confirm) {
  padding: 10px 20px !important;
  font-weight: 500 !important;
}

:global(.apply-prompt-modal .swal2-cancel) {
  padding: 10px 20px !important;
  font-weight: 500 !important;
}
</style>